import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Paper,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Message,
  Schedule,
  Notifications,
  School,
  Person,
  Send,
  Add,
  FilterList,
  Search,
  MarkEmailRead,
  MarkEmailUnread,
  CalendarToday,
  Announcement,
  Emergency,
} from '@mui/icons-material';

interface Teacher {
  id: number;
  name: string;
  subject: string;
  avatar?: string;
  email: string;
}

interface MessageThread {
  id: number;
  teacherId: number;
  teacherName: string;
  subject: string;
  lastMessage: string;
  lastMessageTime: Date;
  unreadCount: number;
  isUrgent: boolean;
  childName: string;
}

interface Meeting {
  id: number;
  teacherId: number;
  teacherName: string;
  subject: string;
  scheduledTime: Date;
  status: 'scheduled' | 'completed' | 'cancelled';
  type: 'parent-teacher' | 'progress-review' | 'concern-discussion';
}

interface Announcement {
  id: number;
  title: string;
  content: string;
  type: 'general' | 'urgent' | 'event' | 'policy';
  publishedDate: Date;
  isRead: boolean;
}

const ParentMessages: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [messageThreads, setMessageThreads] = useState<MessageThread[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<Meeting[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);

  useEffect(() => {
    // Mock data - replace with API calls
    setLoading(true);
    setTimeout(() => {
      const mockTeachers: Teacher[] = [
        { id: 1, name: 'Ms. Sarah Wilson', subject: 'Mathematics', email: 'sarah.wilson@school.edu' },
        { id: 2, name: 'Mr. David Brown', subject: 'English Literature', email: 'david.brown@school.edu' },
        { id: 3, name: 'Dr. Emily Chen', subject: 'Science', email: 'emily.chen@school.edu' },
        { id: 4, name: 'Mrs. Lisa Garcia', subject: 'History', email: 'lisa.garcia@school.edu' },
      ];

      const mockMessageThreads: MessageThread[] = [
        {
          id: 1,
          teacherId: 1,
          teacherName: 'Ms. Sarah Wilson',
          subject: 'Emma\'s Math Progress',
          lastMessage: 'Emma has shown great improvement in algebra. I\'d like to discuss her advanced placement options.',
          lastMessageTime: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
          unreadCount: 2,
          isUrgent: false,
          childName: 'Emma Johnson'
        },
        {
          id: 2,
          teacherId: 2,
          teacherName: 'Mr. David Brown',
          subject: 'Reading Assignment Concern',
          lastMessage: 'Lucas seems to be struggling with the latest reading assignment. Could we schedule a meeting?',
          lastMessageTime: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
          unreadCount: 1,
          isUrgent: true,
          childName: 'Lucas Johnson'
        },
        {
          id: 3,
          teacherId: 3,
          teacherName: 'Dr. Emily Chen',
          subject: 'Science Fair Project',
          lastMessage: 'Thank you for your support with Emma\'s science project. She did an excellent job!',
          lastMessageTime: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
          unreadCount: 0,
          isUrgent: false,
          childName: 'Emma Johnson'
        },
      ];

      const mockMeetings: Meeting[] = [
        {
          id: 1,
          teacherId: 1,
          teacherName: 'Ms. Sarah Wilson',
          subject: 'Mathematics Progress Review',
          scheduledTime: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
          status: 'scheduled',
          type: 'progress-review'
        },
        {
          id: 2,
          teacherId: 2,
          teacherName: 'Mr. David Brown',
          subject: 'Reading Support Discussion',
          scheduledTime: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // 5 days from now
          status: 'scheduled',
          type: 'concern-discussion'
        },
      ];

      const mockAnnouncements: Announcement[] = [
        {
          id: 1,
          title: 'Parent-Teacher Conference Week',
          content: 'Parent-Teacher conferences will be held from March 15-19. Please book your slots through the messaging system.',
          type: 'event',
          publishedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          isRead: false
        },
        {
          id: 2,
          title: 'New Safety Protocol',
          content: 'Updated safety protocols for school pickup and drop-off are now in effect. Please review the attached guidelines.',
          type: 'urgent',
          publishedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          isRead: false
        },
        {
          id: 3,
          title: 'Science Fair Results',
          content: 'Congratulations to all students who participated in the annual science fair. Results and awards will be announced next week.',
          type: 'general',
          publishedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          isRead: true
        },
      ];

      setTeachers(mockTeachers);
      setMessageThreads(mockMessageThreads);
      setUpcomingMeetings(mockMeetings);
      setAnnouncements(mockAnnouncements);
      setLoading(false);
    }, 1000);
  }, []);

  const getMessageStatusColor = (thread: MessageThread) => {
    if (thread.isUrgent) return 'error';
    if (thread.unreadCount > 0) return 'primary';
    return 'default';
  };

  const getMeetingTypeIcon = (type: string) => {
    switch (type) {
      case 'progress-review':
        return <CalendarToday color="primary" />;
      case 'concern-discussion':
        return <Emergency color="warning" />;
      default:
        return <Schedule color="info" />;
    }
  };

  const getAnnouncementColor = (type: string) => {
    switch (type) {
      case 'urgent':
        return 'error';
      case 'event':
        return 'warning';
      case 'policy':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const totalUnreadMessages = messageThreads.reduce((sum, thread) => sum + thread.unreadCount, 0);
  const unreadAnnouncements = announcements.filter(a => !a.isRead).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Typography color="text.primary">Messages</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/parent-dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Communication Center
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Stay connected with teachers and school
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => navigate('/parent/messages/compose')}
          >
            New Message
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<Schedule />}
            onClick={() => navigate('/parent/meetings/schedule')}
          >
            Book Meeting
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={totalUnreadMessages} color="error">
                <Message color="primary" sx={{ fontSize: 40 }} />
              </Badge>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                Messages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalUnreadMessages} unread
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Schedule color="info" sx={{ fontSize: 40 }} />
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                Meetings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {upcomingMeetings.length} upcoming
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={unreadAnnouncements} color="error">
                <Announcement color="warning" sx={{ fontSize: 40 }} />
              </Badge>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                Announcements
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {unreadAnnouncements} new
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Person color="success" sx={{ fontSize: 40 }} />
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                Teachers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teachers.length} contacts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Message />
                Messages
                {totalUnreadMessages > 0 && (
                  <Chip label={totalUnreadMessages} size="small" color="error" />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule />
                Meetings
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Announcement />
                Announcements
                {unreadAnnouncements > 0 && (
                  <Chip label={unreadAnnouncements} size="small" color="error" />
                )}
              </Box>
            } 
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Messages Tab */}
          {activeTab === 0 && (
            <Box>
              {messageThreads.length === 0 ? (
                <Alert severity="info">No message threads yet. Start a conversation with your child's teachers!</Alert>
              ) : (
                <List>
                  {messageThreads.map((thread, index) => (
                    <React.Fragment key={thread.id}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'grey.50' },
                          borderLeft: thread.unreadCount > 0 ? 3 : 0,
                          borderColor: 'primary.main'
                        }}
                        onClick={() => navigate(`/parent/messages/thread/${thread.id}`)}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {thread.teacherName.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: thread.unreadCount > 0 ? 600 : 400 }}>
                                {thread.teacherName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {thread.isUrgent && <Chip label="Urgent" color="error" size="small" />}
                                {thread.unreadCount > 0 && (
                                  <Chip label={thread.unreadCount} color="primary" size="small" />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {formatTimeAgo(thread.lastMessageTime)}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="primary" sx={{ mb: 0.5 }}>
                                Re: {thread.subject} • {thread.childName}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {thread.lastMessage}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < messageThreads.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Meetings Tab */}
          {activeTab === 1 && (
            <Box>
              {upcomingMeetings.length === 0 ? (
                <Alert severity="info">No upcoming meetings scheduled. Book a meeting with your child's teachers!</Alert>
              ) : (
                <Grid container spacing={2}>
                  {upcomingMeetings.map((meeting) => (
                    <Grid item xs={12} md={6} key={meeting.id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {meeting.subject}
                            </Typography>
                            {getMeetingTypeIcon(meeting.type)}
                          </Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            with {meeting.teacherName}
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {meeting.scheduledTime.toLocaleDateString()} at {meeting.scheduledTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" variant="outlined">
                              Reschedule
                            </Button>
                            <Button size="small" variant="contained">
                              Join Meeting
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Announcements Tab */}
          {activeTab === 2 && (
            <Box>
              {announcements.length === 0 ? (
                <Alert severity="info">No announcements at this time.</Alert>
              ) : (
                <Stack spacing={2}>
                  {announcements.map((announcement) => (
                    <Card 
                      key={announcement.id}
                      sx={{ 
                        borderLeft: !announcement.isRead ? 3 : 0,
                        borderColor: getAnnouncementColor(announcement.type) + '.main',
                        opacity: announcement.isRead ? 0.7 : 1
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {announcement.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={announcement.type} 
                              color={getAnnouncementColor(announcement.type) as any}
                              size="small" 
                            />
                            <Typography variant="caption" color="text.secondary">
                              {announcement.publishedDate.toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {announcement.content}
                        </Typography>
                        {!announcement.isRead && (
                          <Button 
                            size="small" 
                            startIcon={<MarkEmailRead />}
                            sx={{ mt: 2 }}
                            onClick={() => {
                              setAnnouncements(prev => 
                                prev.map(a => a.id === announcement.id ? { ...a, isRead: true } : a)
                              );
                            }}
                          >
                            Mark as Read
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ParentMessages;
